/**
 * «Оберту зараз немає» — попап у відповідь на натискання «Крутити», коли
 * оберт цього місяця вже зіграв (owner 2026-09-04: раніше кнопка мовчала).
 *
 * Канон той самий, що в «Квест виконано»: max-w-xs, хрестик, анімований
 * емодзі, заголовок, рядок і одна кнопка.
 */
import { createPortal } from "react-dom";
import { useCountdown } from "@/lib/countdown";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { popupActionsCol, popupBtnPrimary } from "@/lib/popupButtons";

const MONTHS = [
  "січня", "лютого", "березня", "квітня", "травня", "червня",
  "липня", "серпня", "вересня", "жовтня", "листопада", "грудня",
];

/** Конкретна дата, а не «завтра»: відносні слова розходились із лічильником
 *  на головній (owner 2026-09-04). */
function dateLabel(iso?: string): string {
  if (!iso) return "5 числа";
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function WheelNoSpinPopup({
  open,
  onClose,
  nextAt,
  onBuy,
}: {
  open: boolean;
  onClose: () => void;
  nextAt?: string;
  /** Owner 2026-09-23: тут людина найбільше хоче крутити — саме тут і
   *  пропонуємо платні оберти, інакше попап лишається глухим кутом. */
  onBuy?: () => void;
}) {
  // Хук — до раннього виходу, інакше React лається на порядок хуків.
  // Коли до оберту лишилась доба й менше, показуємо живий таймер: дата
  // «5 вересня» ввечері 4-го читалась як «ще не скоро» (owner 2026-09-04).
  const live = useCountdown(nextAt);
  const soon =
    !!nextAt && new Date(nextAt).getTime() - Date.now() < 86400000;

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />
        {/* Без нижнього відступу: під цим блоком кнопки немає, і `mb-4`
            додавав 16px знизу — попап сидів вище центру (owner 2026-09-09:
            «композиція не посередині»). Зверху й знизу тепер однакові 20px
            від `p-5` картки. */}
        <div className="text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="⏳" size={52} />
          </div>
          {/* Owner 2026-09-05: «краще в форматі — зараз недоступно, буде
              доступно через стільки-то». Тому заголовок каже про стан, а не
              докоряє «вже крутив», і далі йде сам відлік. Коли до оберту ще
              більше доби, замість таймера показуємо дату — «через 5 вересня»
              звучало б криво. */}
          <div className="mb-1.5 font-display text-[1.125rem] font-bold leading-tight text-ink [text-wrap:balance]">
            Оберт зараз недоступний
          </div>
          <div className="text-[0.84375rem] leading-relaxed text-ink [text-wrap:balance]">
            {soon
              ? "Наступний буде доступний через"
              : "Наступний оберт даємо"}
          </div>
          <div className="mt-2.5 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 font-display text-[0.8125rem] font-bold text-primary">
            {soon ? live.replace("через ", "") : dateLabel(nextAt)}
          </div>
        </div>
        {onBuy && (
          <div className="mt-4">
            <div className="mb-3 text-center text-[0.78125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
              Чекати не обовʼязково — оберти можна взяти просто зараз.
            </div>
            <div className={popupActionsCol}>
              <button type="button" className={popupBtnPrimary} onClick={onBuy}>
                Ще оберти
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
